/**
 * Why the mission is being held, and by whom (Slice E, CAM-05).
 *
 * A camera sitting has two reasons to pause. A grown-up presses Pause, which is the Phase 1
 * behaviour and belongs to them (GDS §32). Or the camera loses the child, which is GDS §36's first
 * rung and belongs to nobody. The mission runtime has exactly **one** paused state, and this slice
 * does not change it — `src/platform/missions` stays shut — so the composition happens here, where
 * the two causes actually meet.
 *
 * The whole rule: **the mission is paused while any cause holds.** From that, the two contracts a
 * family would notice fall out without a special case for either:
 *
 *   - A grown-up cannot resume into a camera that still cannot see their child. Pressing Resume
 *     clears their own hold; the camera's is still there, the mission stays put, and the overlay
 *     is still on screen telling the child what to do about it.
 *   - A child stepping back into shot does not resume a mission their grown-up paused on purpose.
 *     Parent intent outlives a detector.
 *
 * Pure functions over a small array rather than a `Set` in a ref: the stage re-renders from this,
 * and the transition it has to send the runtime is a function of two values it can both see.
 */

export type StagePauseCause =
  /** A grown-up pressed Pause (or the P key), and only they may lift it. */
  | 'parent'
  /** The camera cannot see the child (CAM-05). Lifted by re-acquisition or a keyboard election. */
  | 'camera';

/** Adds or removes one cause, preserving arrival order and never duplicating. */
export function withPauseCause(
  causes: readonly StagePauseCause[],
  cause: StagePauseCause,
  active: boolean,
): StagePauseCause[] {
  if (!active) return causes.filter((held) => held !== cause);
  return causes.includes(cause) ? [...causes] : [...causes, cause];
}

/**
 * What the runtime must be told, given what was holding before and what holds now.
 *
 * `none` is the interesting answer: it is what makes a two-cause pause compose without the runtime
 * ever learning there was more than one reason.
 */
export function pauseTransition(
  previous: readonly StagePauseCause[],
  next: readonly StagePauseCause[],
): 'pause' | 'resume' | 'none' {
  const wasPaused = previous.length > 0;
  const isPaused = next.length > 0;
  if (wasPaused === isPaused) return 'none';
  return isPaused ? 'pause' : 'resume';
}
